import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/Bits";
import { SectionLabel } from "@/components/SectionLabel";
import { Field, TextAreaField, Honeypot, SuccessScreen, SubmitButton } from "@/components/FormKit";
import { postJSON } from "@/lib/api";
import { CONTACT } from "@/data/content";

export default function Contact() {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await postJSON("/contact", { ...f, company });
      setDone(true);
      toast.success("Message sent.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        n="10"
        label="Contact"
        lines={["Reach out for support", "or inquiries"]}
        intro="Questions about the framework, enrollment or the NextGen Training Program? We'd love to hear from you."
      />

      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-12 gap-16">
          <div className="md:col-span-5">
            <SectionLabel n="◆">Direct lines</SectionLabel>
            <div className="mt-8 space-y-6">
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="group flex items-center gap-4 border-b border-white/10 pb-5 hover:border-accent transition-colors">
                <Mail className="w-6 h-6 text-accent" />
                <span className="text-white text-lg">{CONTACT.email}</span>
                <ArrowUpRight className="w-4 h-4 text-white/40 ml-auto group-hover:text-accent group-hover:rotate-45 transition-all" />
              </a>
              <a href={`tel:${CONTACT.phone}`} data-testid="contact-phone-link" className="group flex items-center gap-4 border-b border-white/10 pb-5 hover:border-accent transition-colors">
                <Phone className="w-6 h-6 text-accent" />
                <span className="text-white text-lg">{CONTACT.phone}</span>
                <ArrowUpRight className="w-4 h-4 text-white/40 ml-auto group-hover:text-accent group-hover:rotate-45 transition-all" />
              </a>
            </div>
            <div className="flex flex-wrap gap-3 mt-10">
              {CONTACT.socials.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noreferrer" className="border border-white/20 px-5 py-2.5 font-mono-label text-[11px] tracking-label uppercase text-white/70 hover:bg-accent hover:text-[#050B14] hover:border-accent transition-colors">
                  {s.label}
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-7">
            {done ? (
              <SuccessScreen testid="contact-success" title="Message sent." body="Thank you for reaching out. We'll respond to your inquiry as soon as possible." />
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <Honeypot value={company} onChange={(e) => setCompany(e.target.value)} />
                <div className="grid sm:grid-cols-2 gap-8">
                  <Field label="Name" id="name" value={f.name} onChange={set("name")} placeholder="Your name" required />
                  <Field label="Email" id="email" type="email" value={f.email} onChange={set("email")} placeholder="you@email.com" required />
                </div>
                <Field label="Phone" id="phone" value={f.phone} onChange={set("phone")} placeholder="Optional" />
                <TextAreaField label="Message" id="message" value={f.message} onChange={set("message")} placeholder="How can we help?" required />
                <SubmitButton loading={loading} testid="contact-submit">Send Message</SubmitButton>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
